import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentItem } from '../../components/DocumentRow';
import type { HealthTestItem } from '../../components/HealthTestsSection';
import { dogInitial } from '../../utils/dogAvatar';
import { formatDate as formatDateIntl, formatDateTime, parseServerDate } from '../../utils/formatDate';

export interface AdminStats {
  totalUsers: number;
  totalDogs: number;
  totalBreedingStations: number;
  totalBreedingPlans: number;
  totalBreeds: number;
  activeBackgroundTasks: number;
  failedBackgroundTasks: number;
  totalOcrDocuments: number;
  pendingModernBertDocuments: number;
  modernBertAvailable: boolean;
  totalDocumentTypes: number;
  pregnancyDaysWithContent: number;
  totalGeneticConditions: number;
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  isActive: boolean;
  createdAt: string;
  roles: string[];
}

/** Alle definierten Anwendungsrollen in der Reihenfolge ihrer Berechtigungsstufe. */
export const ALL_ROLES = ['Hundehalter', 'Zuechter', 'Zuchtverband', 'Support', 'DnaAuswertung', 'Admin'] as const;

/** Liefert den lokalisierten Anzeigenamen einer Rolle. */
export const adminRoleLabel = (role: string, t: (key: string) => string): string => {
  const key = `admin:roles.${role}`;
  const label = t(key);
  // Falls kein Uebersetzungsschluessel vorhanden ist, gibt i18n den Schluessel selbst zurueck
  return label === key ? role : label;
};

export interface AdminDog {
  id: number;
  name: string;
  breedName?: string;
  sex: string;
  dateOfBirth?: string;
  ownerId: string;
  ownerEmail?: string;
  ownerName?: string;
  photoUrl?: string;
  isActive: boolean;
  createdAt: string;
  hasDnaProfile: boolean;
}

/** Antwort von GET /admin/dogs/{id}: der Hund mit allem, was die Hundeseite in Einzelabfragen laedt. */
export interface AdminDogDetailData {
  dog: {
    id: number;
    name: string;
    breedNameGerman: string;
    breedNameEnglish: string;
    sex: string;
    dateOfBirth: string | null;
    dateOfDeath: string | null;
    nicknames: string | null;
    characteristics: string | null;
    healthNotes: string | null;
    description: string | null;
    photoUrl: string | null;
    microchipNumber: string | null;
    shsbLosNumber: string | null;
    holderName: string | null;
    isBreedingApproved: boolean;
    isNeutered: boolean;
    neuteredDate: string | null;
    hasSemenAvailable: boolean;
    color: string | null;
    collarColor: string | null;
    breedingLocation: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    ownerName: string | null;
  };
  ownerId: string;
  ownerEmail?: string;
  /** `weight` immer in Kilogramm, `unit` nur die Einheit der Erfassung. */
  weightEntries: Array<{ id: number; weight: number; unit: string | null; measuredAt: string; notes: string | null }>;
  events: Array<{
    id: number;
    type: string;
    vaccinationType: string | null;
    description: string | null;
    date: string;
    startTime: string | null;
    location: string | null;
    notes: string | null;
  }>;
  documents: DocumentItem[];
  healthTests: HealthTestItem[];
  heatCycles: Array<{
    id: number;
    startDate: string;
    endDate: string | null;
    notes: string | null;
    expectedBirthDate: string | null;
    matingRecords: Array<{ id: number; matingDate: string; matingDurationMinutes: number | null; studBoyName: string | null }>;
    progesteroneEntries: Array<{ id: number; value: number; unit: string | null; measuredAt: string; notes: string | null }>;
  }>;
  photos: Array<{ id: number; photoUrl: string; createdAt: string }>;
}

/** Navigationsstate fuer /admin/dogs/:id: wer aus der Hundeliste kommt, soll dorthin zurueck statt zum Besitzer. */
export interface AdminDogDetailLocationState {
  from?: 'dogList';
}

export const useDogSexLabel = () => {
  const { t } = useTranslation();
  return (sex: string) => (sex === 'Male' ? t('dogs:sex.male') : sex === 'Female' ? t('dogs:sex.female') : t('dogs:sex.unknown'));
};

/** Rundes Hunde-Avatar: Portrait oder, ohne Foto, die Initiale. Groesse und Schrift kommen per className. */
export const AdminDogAvatar: React.FC<{ name: string; photoUrl?: string | null; className: string }> = ({ name, photoUrl, className }) =>
  photoUrl ? (
    <img src={photoUrl} alt="" className={`rounded-full object-cover ${className}`} />
  ) : (
    <span className={`avatar-badge ${className}`}>{dogInitial(name)}</span>
  );

export interface AdminStation {
  id: number;
  name: string;
  city?: string;
  country?: string;
  ownerEmail?: string;
  memberCount: number;
  isActive: boolean;
  createdAt: string;
}

export interface AdminPlan {
  id: number;
  name?: string | null;
  dogName?: string;
  studBoyName?: string;
  plannedDate?: string;
  expectedDeliveryDate?: string;
  createdAt: string;
}

export interface AdminBreed {
  id: number;
  name: string;
  nameGerman: string;
  nameEnglish: string;
  group?: string;
  origin?: string;
  isActive: boolean;
}

export interface AdminDocumentType {
  id: number;
  /** Schlüssel ist die Sprache (de/en/fr/it); fehlende Übersetzungen sind leer. */
  translations: Record<string, string>;
  /** Stabiler englischer Schlüssel fuer den ModernBERT-Trainingsexport (z.B. "vaccination_certificate"), oder
   * leer, wenn noch keiner vergeben ist. */
  key: string | null;
  isUsed: boolean;
}

/** Name in der aktiven Sprache, sonst der deutsche - wie ihn auch Nutzer sehen. */
export const documentTypeDisplayName = (documentType: AdminDocumentType, language: string) =>
  documentType.translations[language]?.trim() || documentType.translations.de;

export interface BreedSyncResult {
  addedCount: number;
  addedBreeds: string[];
  totalCount: number;
}

export type BackgroundTaskStatusValue = 'Created' | 'Running' | 'Completed' | 'Failed';

export interface AdminBackgroundTaskStep {
  stepIndex: number;
  name: string;
  status: BackgroundTaskStatusValue;
  message?: string;
  startedAt?: string;
  completedAt?: string;
  durationSeconds?: number;
}

export interface AdminBackgroundTask {
  id: number;
  type: string;
  status: BackgroundTaskStatusValue;
  errorMessage?: string;
  attemptCount: number;
  userEmail?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  nextAttemptAt?: string;
  durationSeconds?: number;
  averageDurationSeconds?: number;
  lockedBy?: string;
  steps: AdminBackgroundTaskStep[];
}

export interface AdminOcrDocument {
  kind: 'Dog' | 'BreedingPlan';
  documentId: number;
  ownerId: number;
  ownerLabel: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  hasMarkdown: boolean;
  queuedForOcr: boolean;
  /** Von der Nutzerin/dem Nutzer beim Hochladen ausgewaehlte Dokumentart, oder null/undefined ohne Auswahl. */
  documentTypeId?: number | null;
  hasModernBertResult: boolean;
  modernBertLabel?: string;
  modernBertConfidence?: number;
  queuedForModernBert: boolean;
}

export const ACTIVE_BACKGROUND_TASK_STATUSES: BackgroundTaskStatusValue[] = ['Created', 'Running'];

export interface AdminNode {
  id: number;
  name: string;
  description?: string;
  isBackgroundTaskRunnerEnabled: boolean;
  lastSeenAt?: string;
  createdAt: string;
}

export const BACKGROUND_TASK_STATUS_VARIANT: Record<BackgroundTaskStatusValue, 'secondary' | 'warning' | 'success' | 'error'> = {
  Created: 'secondary',
  Running: 'warning',
  Completed: 'success',
  Failed: 'error',
};

export const formatAdminDate = (value?: string) => (value ? formatDateIntl(value) : '-');
export const formatAdminDateTime = (value?: string) => (value ? formatDateTime(value) : '-');

export const formatDurationSeconds = (totalSeconds: number) => {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
};

/** Sekundentakt fuer die verstrichene Zeit laufender Aufgaben - nur solange `active`, damit
 *  Seiten ohne laufende Aufgabe nicht dauerhaft rendern. */
export const useNowTicker = (active: boolean) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [active]);
  return now;
};

/** Label/Wert-Zeile. Die Schriftgroesse kommt vom Container, damit dieselbe Zeile in den
 *  kompakten Listen-Karten und auf den Detailseiten passt. */
export const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="shrink-0 text-dark-400">{label}</span>
    <span className="flex flex-wrap items-center justify-end gap-1 text-right text-dark-200">{children}</span>
  </div>
);

export const TaskDuration: React.FC<{ task: AdminBackgroundTask; now: number }> = ({ task, now }) => {
  const { t } = useTranslation();

  if (task.status === 'Completed' || task.status === 'Failed') {
    return <span className="text-dark-300">{task.durationSeconds != null ? formatDurationSeconds(task.durationSeconds) : '-'}</span>;
  }

  if (task.status === 'Running' && task.startedAt) {
    const elapsedSeconds = Math.max(0, (now - parseServerDate(task.startedAt).getTime()) / 1000);
    const progressPercent =
      task.averageDurationSeconds != null && task.averageDurationSeconds > 0
        ? Math.min(96, (elapsedSeconds / task.averageDurationSeconds) * 100)
        : null;
    return (
      <div className="min-w-[8rem] space-y-1">
        <div className="flex items-center justify-between gap-2 text-xs text-dark-300">
          <span>{formatDurationSeconds(elapsedSeconds)}</span>
          {task.averageDurationSeconds != null && (
            <span className="text-dark-500">
              {t('admin:backgroundTasks.averageDurationShort', { duration: formatDurationSeconds(task.averageDurationSeconds) })}
            </span>
          )}
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-dark-700">
          {progressPercent != null ? (
            <div className="h-full rounded-full bg-primary-400 transition-all duration-1000 ease-linear" style={{ width: `${progressPercent}%` }} />
          ) : (
            <div className="h-full w-1/3 animate-pulse rounded-full bg-primary-400/60" />
          )}
        </div>
      </div>
    );
  }

  return <span className="text-dark-500">-</span>;
};

export const stepDurationLabel = (step: AdminBackgroundTaskStep, now: number) => {
  if (step.status === 'Completed' || step.status === 'Failed') {
    return step.durationSeconds != null ? formatDurationSeconds(step.durationSeconds) : null;
  }
  if (step.status === 'Running' && step.startedAt) {
    return formatDurationSeconds(Math.max(0, (now - parseServerDate(step.startedAt).getTime()) / 1000));
  }
  return null;
};
